import React from 'react'

import { gql } from '@apollo/client'

import * as f from '../../components/BPMTable/BPMInputs'
import {
  DataMapper,
  DataTransformer,
  NumberComponentCreator,
  TableFieldDescription,
  StatusComponentCreator,
  BooleanExcelDisplayCreator,
  DateTimeComponentCreator,
  DateTimeDisplayCreator,
  DateComponentCreator,
  DateDisplayCreator,
  DefaultComponentCreator,
} from '../../components/BPMTable/TableMapperCreator'
import { GraphQLDownloadFile } from '../../components/FileDownloader/FileComponentDownloader'

const tableQuery = gql`
  query Bayphones(
    $orderField: String
    $first: Int
    $last: Int
    $after: String
    $before: String
    $enterpriseId: String
    $email: String
    $firstName: String
    $lastName: String
    $deviceType: String
    $state: String
    $changeReason: String
    $closeReason: String
    $onHoldReason: String
    $expirationDate: String
    $validSince: String
    $observation: String
    $createdAt: String
    $updatedAt: String
    $createdBy: String
    $updatedBy: String
    $closedAt: String
    $id: String
  ) {
    bayphones(
      orderField: $orderField
      first: $first
      last: $last
      after: $after
      before: $before
      enterpriseId: $enterpriseId
      email: $email
      firstName: $firstName
      lastName: $lastName
      deviceType: $deviceType
      state: $state
      changeReason: $changeReason
      closeReason: $closeReason
      onHoldReason: $onHoldReason
      expirationDate: $expirationDate
      validSince: $validSince
      observation: $observation
      createdAt: $createdAt
      updatedAt: $updatedAt
      createdBy: $createdBy
      updatedBy: $updatedBy
      closedAt: $closedAt
      id: $id
    ) {
      edges {
        node {
          id
          user {
            id
            sales
            user {
              id
              enterpriseId
              email
              isActive
              dateHired
              firstName
              lastName
            }
            company {
              id
              currency {
                code
                symbol
              }
            }
          }
          deviceType {
            id
            name
          }
          state {
            id
            name
            identifier
          }
          changeReason {
            id
            name
          }
          onHoldReason {
            id
            name
          }
          closeReason {
            id
            name
          }
          claimedAmount
          approvedAmount
          validSince
          closedAt
          expirationDate
          termsFile
          concurTicket
          observation
          createdAt
          updatedAt
          createdBy {
            id
            email
            firstName
            lastName
          }
          updatedBy {
            id
            email
            firstName
            lastName
          }
        }
      }
      count
      pageInfo {
        hasNextPage
        startCursor
        endCursor
      }
    }
  }
`

const editMutator = gql`
    mutation editBayphoneMutation(
        $id:String!,
        $observation:String,
    ) {
        editBayphone(
            id:$id,
            observation:$observation,
        ) {
      bayphone {
        id
      }
    }
  }
`

const newMutator = gql`
  mutation createBayphoneMutation(
        $user:String!,
        $deviceType:String!,
        $changeReason:String!,
        $observation:String,
        $validSince:Date!,
        $expirationDate:Date!,
  ) {
    createBayphone(
            user:$user,
            deviceType:$deviceType,
            changeReason:$changeReason,
            observation:$observation,
            validSince:$validSince,
            expirationDate:$expirationDate,
    ) {
      bayphone {
        id
      }
    }
  }
`

const deleteMutator = null

const unitQuery = `
    bayphone(id: $id) {
        id
        user {
            id
            sales
            user {
                id
                enterpriseId
                email
                isActive
                dateHired
                firstName
                lastName
            }
            company {
                id
                currency {
                    code
                    symbol
                }
            }
        }
        deviceType {
            id
            name
        }
        state {
            id
            name
            identifier
        }
        changeReason {
            id
            name
        }
        onHoldReason {
            id
            name
        }
        closeReason {
            id
            name
        }
        claimedAmount
        approvedAmount
        expirationDate
        closedAt
        validSince
        termsFile
        concurTicket
        observation
        createdAt
        updatedAt
        createdBy {
            id
            email
            firstName
            lastName
        }
        updatedBy {
            id
            email
            firstName
            lastName
        }
    }
`



const bayphoneQueries = { tableQuery, editMutator, newMutator, deleteMutator, unitQuery }


class TermsFileComponentCreator extends DefaultComponentCreator {
  createWith(data, rowData, fieldAlign) {
        return data.termsFile ? <GraphQLDownloadFile resourceType='bayphone_terms' id={data.id} /> : null
  }
}

// TODO - Refactor and rename this function. Avoid mapping and unnecessary manipulation.
//  Just create an array of TableFieldDescription objects and filter excluded by id.
const getBayphonesTableDefinitions = (filterByUser) => {
  const fields = {
        id: new TableFieldDescription(
            'id',
            new DataMapper("id"),
            new f.FilterDescription(new f.TextFilter("id")),
            { label: 'Id' }
        ),
    enterpriseId: new TableFieldDescription(
      'enterpriseId',
            new DataMapper("user.user.enterpriseId"),
            new f.FilterDescription(new f.TextFilter("enterpriseId")),
      { label: 'Enterprise Id' }
    ),
    email: new TableFieldDescription(
      'email',
            new DataMapper("user.user.email"),
            new f.FilterDescription(new f.TextFilter("email")),
      { label: 'Email' }
    ),
    firstName: new TableFieldDescription(
      'firstName',
            new DataMapper("user.user.firstName"),
            new f.FilterDescription(new f.TextFilter("firstName")),
      { label: 'First Name' }
    ),
    lastName: new TableFieldDescription(
      'lastName',
            new DataMapper("user.user.lastName"),
            new f.FilterDescription(new f.TextFilter("lastName")),
      { label: 'Last Name' }
    ),
    sales: new TableFieldDescription(
      'sales',
            new DataMapper("user.sales"),
            new f.FilterDescription(new f.BooleanFilter("sales", "Yes", "No")),
      {
        label: 'Sales',
        componentCreator: new StatusComponentCreator(),
                excelDisplay: new BooleanExcelDisplayCreator() 
      }
    ),
    isActive: new TableFieldDescription(
      'isActive',
            new DataMapper("user.user.isActive"),
            new f.FilterDescription(new f.BooleanFilter("isActive", "Yes", "No")),
      {
        label: 'Status Employee',
        componentCreator: new StatusComponentCreator(),
                excelDisplay: new BooleanExcelDisplayCreator() 
      }
    ),
    deviceType: new TableFieldDescription(
      'deviceType',
            new DataMapper("deviceType.name"),
            new f.FilterDescription(new f.TextFilter("deviceType")),
      { label: 'Device Type' }
    ),
    validSince: new TableFieldDescription(
      'validSince',
            new DataMapper("validSince"),
            new f.FilterDescription(new f.PeriodFilter("validSince")),
            { label: 'Valid since',
                componentCreator: new DateComponentCreator(),
                excelDisplay: new DateDisplayCreator(),
            }
    ),
    expirationDate: new TableFieldDescription(
      'expirationDate',
            new DataMapper("expirationDate"),
            new f.FilterDescription(new f.PeriodFilter("expirationDate")),
            { label: 'Expiration Date',
                componentCreator: new DateComponentCreator(),
                excelDisplay: new DateDisplayCreator(),
            }
    ),
    closedAt: new TableFieldDescription(
      'closedAt',
            new DataMapper("closedAt"),
            new f.FilterDescription(new f.PeriodFilter("closedAt")),
            { label: 'Closed At',
                componentCreator: new DateComponentCreator(),
                excelDisplay: new DateDisplayCreator(),
            }
    ),
    state: new TableFieldDescription(
      'state',
            new DataMapper("state.name"),
            new f.FilterDescription(new f.TextFilter("state")),
      { label: 'State' }
    ),
        stateIdentifier: new TableFieldDescription(
            'stateIdentifier',
            new DataMapper("state.identifier"),
            null,
            { label: 'State Identifier', hidenField: true }
        ),
    changeReason: new TableFieldDescription(
      'changeReason',
            new DataMapper("changeReason.name"),
            new f.FilterDescription(new f.TextFilter("changeReason")),
      { label: 'Reason' }
    ),
    closeReason: new TableFieldDescription(
      'closeReason',
            new DataMapper("closeReason.name"),
            new f.FilterDescription(new f.TextFilter("closeReason")),
      { label: 'Close reason' }
    ),
    onHoldReason: new TableFieldDescription(
      'onHoldReason',
            new DataMapper("onHoldReason.name"),
            new f.FilterDescription(new f.TextFilter("onHoldReason")),
      { label: 'On hold reason' }
    ),
    termsFile: new TableFieldDescription(
      'termsFile',
            new DataMapper("id", "termsFile"),
      new f.FilterDescription(new f.NullCreator()),
      {
        label: 'Terms File',
        hideSort: true,
                transform: new DataTransformer((data)=>data),
        componentCreator: new TermsFileComponentCreator(),
      }
    ),
    claimedAmount: new TableFieldDescription(
      'claimedAmount',
            new DataMapper("claimedAmount", "user.company.currency.symbol"),
            new f.FilterDescription(new f.NullCreator("claimedAmount")),
      {
        label: 'Claimed Amount',
        componentCreator: new NumberComponentCreator('', 2),
                transform: new DataTransformer(({claimedAmount, symbol})=>({value: Number(claimedAmount).toFixed(2), symbol: symbol})),
                excelTransform: new DataTransformer(({claimedAmount, symbol})=>(Number(claimedAmount).toFixed(2))),
                fieldAlign: 'right'
      }
    ),
    approvedAmount: new TableFieldDescription(
      'approvedAmount',
            new DataMapper("approvedAmount", "user.company.currency.symbol"),
            new f.FilterDescription(new f.NullCreator("approvedAmount")),
      {
        label: 'Approved Amount',
        componentCreator: new NumberComponentCreator('', 2),
                transform: new DataTransformer(({approvedAmount, symbol})=>({value: Number(approvedAmount).toFixed(2), symbol: symbol})),
                excelTransform: new DataTransformer(({approvedAmount, symbol})=>(Number(approvedAmount).toFixed(2))),
                fieldAlign: 'right'
      }
    ),
    observation: new TableFieldDescription(
      'observation',
            new DataMapper("observation"),
            new f.FilterDescription(new f.TextFilter("observation")),
      { label: 'Observation', hideSort: true }
    ),
    createdAt: new TableFieldDescription(
      'createdAt',
            new DataMapper("createdAt"),
            new f.FilterDescription(new f.DateFilter("createdAt")),
      {
        label: 'Created At',
        onlyForm: true,
        componentCreator: new DateTimeComponentCreator(),
                excelDisplay: new DateTimeDisplayCreator()
      }
    ),
    createdBy: new TableFieldDescription(
      'createdBy',
            new DataMapper("createdBy.email"),
            new f.FilterDescription(new f.TextFilter("createdBy")),
      { label: 'Created By', onlyForm: true }
    ),
    updatedAt: new TableFieldDescription(
      'updatedAt',
            new DataMapper("updatedAt"),
            new f.FilterDescription(new f.DateFilter("updatedAt")),
      {
        label: 'Updated At',
        onlyForm: true,
        componentCreator: new DateTimeComponentCreator(),
                excelDisplay: new DateTimeDisplayCreator()
      }
    ),
    updatedBy: new TableFieldDescription(
      'updatedBy',
            new DataMapper("updatedBy.email"),
            new f.FilterDescription(new f.TextFilter("updatedBy")),
      { label: 'Updated By', onlyForm: true }
        )
  };

  var excludedFields = [];
  if (filterByUser) {
        excludedFields = ['enterpriseId', 'email', 'firstName', 'lastName', 'userIsActive', 'createdBy', 'updatedBy', 'createdAt', 'updatedAt'];
  }

  return Object.entries(fields)
    .filter(([key]) => !excludedFields.includes(key))
    .map(([key, value]) => ({ ...value }));
}

export { getBayphonesTableDefinitions, bayphoneQueries}
